import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Target, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";

export function useFocus() {
  return useQuery({ queryKey: ["focus"], queryFn: () => api.get("/focus").then((r) => r.data), staleTime: 30_000 });
}

export function ActiveFocus() {
  const qc = useQueryClient();
  const { data } = useFocus();
  if (!data?.active.length) return null;
  const setStatus = async (id, status) => {
    try {
      await api.patch(`/focus/${id}`, { status });
      qc.invalidateQueries({ queryKey: ["focus"] });
      qc.invalidateQueries({ queryKey: ["analytics"] });
      toast.success(status === "completed" ? "Marked as completed" : "Focus removed");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <div className="card p-4 mb-4 space-y-3" data-testid="log-active-focus">
      <div className="eyebrow flex items-center gap-1.5"><Target size={12} className="text-[#E50914]" /> Your focus this session</div>
      {data.active.map((f) => (
        <div key={f.id} className="flex gap-3" data-testid={`log-focus-${f.id}`}>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">{f.title}</div>
            {f.drill && <div className="text-[12.5px] text-[#A1A1AA] mt-0.5">{f.drill}</div>}
          </div>
          <div className="flex gap-1 shrink-0">
            <button onClick={() => setStatus(f.id, "completed")} aria-label="Mark completed" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-[#22C55E]" data-testid={`log-focus-complete-${f.id}`}><Check size={15} /></button>
            <button onClick={() => setStatus(f.id, "dismissed")} aria-label="Dismiss" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-[#A1A1AA]" data-testid={`log-focus-dismiss-${f.id}`}><X size={15} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function CheckinRow({ focus, sessionId }) {
  const qc = useQueryClient();
  const done = focus.checkins.find((c) => c.session_id === sessionId);
  const [choice, setChoice] = useState(done?.practised || null);
  const [note, setNote] = useState(done?.note || "");
  const [saved, setSaved] = useState(!!done);
  const save = async (practised, n = note) => {
    setChoice(practised);
    try {
      await api.post(`/focus/${focus.id}/checkin`, { session_id: sessionId, practised, note: n });
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["focus"] });
      qc.invalidateQueries({ queryKey: ["analytics"] });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const opts = [["yes", "Yes"], ["partly", "A bit"], ["no", "Not today"]];
  return (
    <div className="space-y-2" data-testid={`checkin-${focus.id}`}>
      <div className="text-sm">Did you work on <span className="font-semibold">{focus.title}</span>?</div>
      <div className="grid grid-cols-3 gap-2">
        {opts.map(([v, l]) => (
          <button key={v} onClick={() => save(v)} className={`h-9 rounded-lg text-sm border ${choice === v ? "border-[#E50914] bg-[#E50914]/15" : "border-[#27272A] bg-white/[0.03]"}`} data-testid={`checkin-${focus.id}-${v}`}>{l}</button>
        ))}
      </div>
      {choice && choice !== "no" && (
        <div className="flex gap-2">
          <input value={note} onChange={(e) => { setNote(e.target.value); setSaved(false); }} maxLength={500} placeholder="What happened? (optional)" className="flex-1 h-9 rounded-lg bg-[#111113] border border-[#27272A] px-3 text-sm" data-testid={`checkin-${focus.id}-note`} />
          <button onClick={() => save(choice)} disabled={saved} className="h-9 px-3 rounded-lg bg-white/10 text-sm disabled:opacity-50" data-testid={`checkin-${focus.id}-save`}>{saved ? "Saved" : "Save"}</button>
        </div>
      )}
    </div>
  );
}

export function FocusCheckin({ sessionId }) {
  const { data } = useFocus();
  if (!data?.active.length) return null;
  return (
    <div className="card p-4 space-y-4" data-testid="log-focus-checkin">
      <div className="eyebrow flex items-center gap-1.5"><Target size={12} className="text-[#E50914]" /> Focus check-in</div>
      {data.active.map((f) => <CheckinRow key={f.id} focus={f} sessionId={sessionId} />)}
    </div>
  );
}
